import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";

// Self-hosted fonts via Fontsource. These packages bundle the font
// files locally and ship per-weight CSS files that emit @font-face
// declarations. We deliberately do NOT link to Google Fonts' CDN —
// that would leak visitor info to a third party (HIPAA-adjacent
// concern) and add a DNS lookup before first paint. Only the weights
// we actually use get imported; add more weights here as they're
// needed by the UI.
//
// These are the PLATFORM faces — one pairing app-wide, no module owns
// its own typography: Barlow Condensed is display only (page titles,
// KPI numerals — never below 20px, per the metrics rule in
// styles/tokens.css.ts); Nunito Sans carries body, UI, and every
// heading under that floor.
import "@fontsource/barlow-condensed/600.css";
import "@fontsource/barlow-condensed/700.css";
import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";

// Side-effect imports for global styles. Order matters: tokens first
// (establishes the :root custom-property layer), then reset (element
// defaults), then base (typed element styles), and finally the
// reduced-motion rule, which must come last so its !important
// declarations sit at the end of the cascade. Everything downstream
// reads from the token layer via `vars`.
import "~/styles/tokens.css";
import "~/styles/reset.css";
import "~/styles/base.css";
import "~/styles/reduced-motion.css";

import { queryClient } from "~/lib/queryClient";
import { router } from "~/router";

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("root element missing from index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
